/* Created by Interactive 63 - https://interactive63.com */
/**
 * The inspector's gradient stop section: where the stop sits along its gradient, and buttons to
 * add a stop after it or delete it.
 */
import type { ElementId } from '../../../core/model/types';
import { readStopOffset } from '../../../core/properties/stops';
import { addStopAfterSelected, deleteSelectedStops, moveStop } from '../../store/stop-actions';
import { reportArrange } from '../../store/layer-actions';
import type { Store } from '../../store/store';
import { h } from '../../ui/dom';
import { NumberInput } from './inputs/number-input';

export class StopSection {
  readonly el: HTMLElement;
  private readonly offset: NumberInput;

  /** Builds the section for one stop. */
  constructor(
    private readonly store: Store,
    private readonly id: ElementId,
  ) {
    this.offset = new NumberInput({
      label: 'Offset',
      ariaLabel: 'Stop offset along the gradient',
      unit: '%',
      step: 1,
      min: 0,
      max: 100,
      scrubKey: `scrub:stop-offset:${id}`,
      onChange: (value, key) => {
        moveStop(this.store, this.id, value, key);
      },
    });
    const add = h(
      'button',
      { type: 'button', title: 'Add a stop between this one and the next' },
      'Add stop after',
    );
    add.addEventListener('click', () => {
      reportArrange(addStopAfterSelected(this.store));
    });
    const remove = h('button', { type: 'button', title: 'Delete this stop' }, 'Delete stop');
    remove.addEventListener('click', () => {
      reportArrange(deleteSelectedStops(this.store));
    });
    this.el = h(
      'section',
      { class: 'inspector-section stop-section', 'aria-label': 'Gradient stop' },
      h('div', { class: 'keyframe-field' }, this.offset.el),
      h('div', { class: 'element-toggles' }, add, remove),
    );
    this.refresh();
  }

  /** Updates the offset from the artwork. Edits stop at the neighbouring stops. */
  refresh(): void {
    const { source } = this.store.getState().project.svg;
    this.offset.setValue(readStopOffset(source, this.id));
  }
}
